import { Controller } from '@hotwired/stimulus'

const FORM_SELECTOR = '#event_details_form, #payment_form'

export default class extends Controller {
  static targets = ['display', 'countdown', 'expired']

  static values = { expiresAt: String, expiredUrl: String }

  connect() {
    const expiresAt = new Date(this.expiresAtValue)
    if (isNaN(expiresAt.getTime())) return

    this.remaining = Math.floor((expiresAt.getTime() - Date.now()) / 1000.0)

    if (this.remaining <= 0) {
      this.element.classList.add('hidden')
      return
    }

    // Attendee fields are prefilled by JS after connect, so an immediate baseline would read that prefill as user input.
    this.baseline = null
    this.baselineFrame = requestAnimationFrame(() => this._captureBaseline())

    this.startedAt = this._monotonicNow()
    this._tick()
    this.timer = setInterval(() => this._tick(), 1000)
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    if (this.baselineFrame) cancelAnimationFrame(this.baselineFrame)
    this.baselineFrame = null
  }

  _tick() {
    const left = this._secondsLeft()

    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = this._format(left)
    }

    if (left > 0) return

    this.disconnect()

    if (!this._hasUserInput()) {
      if (this.expiredUrlValue) window.location = this.expiredUrlValue
      return
    }

    this._showExpiredNotice()
  }

  _showExpiredNotice() {
    if (this.hasCountdownTarget) this.countdownTarget.classList.add('hidden')
    if (this.hasExpiredTarget) this.expiredTarget.classList.remove('hidden')

    // elements, not querySelectorAll: the Enter-key submit is form-associated but sits outside the form.
    this._forms().forEach((form) =>
      Array.from(form.elements)
        .filter((element) => element.type === 'submit')
        .forEach((button) => (button.disabled = true)),
    )
  }

  _secondsLeft() {
    const elapsed = Math.floor((this._monotonicNow() - this.startedAt) / 1000.0)
    return Math.max(this.remaining - elapsed, 0)
  }

  _format(distance) {
    if (distance <= 0) return '0:00'

    const seconds = Math.floor(distance % 60)
    const minutes = Math.floor(distance / 60)

    return `${minutes}:${seconds < 10 ? `0${seconds}` : String(seconds)}`
  }

  _captureBaseline() {
    this.baselineFrame = null
    this.baseline = new WeakMap()
    this._fields().forEach((field) =>
      this.baseline.set(field, this._stateOf(field)),
    )
  }

  _forms() {
    return Array.from(document.querySelectorAll(FORM_SELECTOR))
  }

  _fields() {
    return this._forms()
      .flatMap((form) =>
        Array.from(form.querySelectorAll('input, select, textarea')),
      )
      .filter((field) => !field.disabled && field.type !== 'hidden')
  }

  _stateOf(field) {
    if (field.type === 'checkbox' || field.type === 'radio')
      return String(field.checked)
    if (field.tagName === 'SELECT') {
      return Array.from(field.selectedOptions)
        .map((option) => option.value)
        .join(',')
    }

    return field.value
  }

  _hasUserInput() {
    const active = document.activeElement
    if (
      active &&
      active.closest(FORM_SELECTOR) &&
      /^(INPUT|SELECT|TEXTAREA|IFRAME)$/.test(active.tagName)
    ) {
      return true
    }

    if (!this.baseline) return false

    return this._fields().some(
      (field) =>
        this.baseline.has(field) &&
        this._stateOf(field) !== this.baseline.get(field),
    )
  }

  _monotonicNow() {
    return window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : Date.now()
  }
}
